import { List, IconButton, WithStyles, withStyles } from '@material-ui/core/'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import DirectionsBoatIcon from '@material-ui/icons/DirectionsBoat'
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus'
import SubwayIcon from '@material-ui/icons/Subway'
import TrainIcon from '@material-ui/icons/Train'
import TramIcon from '@material-ui/icons/Tram'
import PedalBikeIcon from '@mui/icons-material/PedalBike'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Stops_LineTypeEnum } from 'src/api/gen'

interface ILayersContent {
  label: string
  layer: React.ReactNode
}

export enum DockedStationEnum {
  'citibike' = 'citibike',
  'unknown' = 'unknown',
}

interface ILayersPopup extends WithStyles {
  activeDockedStationLayers?: DockedStationEnum[]
  activeStopsLayers: Stops_LineTypeEnum[]
  toggleDockedStationLayer?: (station: DockedStationEnum) => void
  toggleLayer: (layer: Stops_LineTypeEnum) => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'absolute',
      bottom: 25,
      right: 164,
      boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
      borderRadius: 4,
      display: 'flex',
      flexDirection: 'column',
      zIndex: theme.zIndex.drawer,
      margin: '5px',
      minWidth: 50,
      minHeight: 30,
      maxWidth: '300px',
      background: '#FFF',
      alignItems: 'left',
    },
    title: {
      fontSize: '16px',
      lineHeight: '30px',
      fontWeight: 'bold',
      color: theme.palette.grey[900],
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '12px',
    },
    subtitle: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 'bold',
      color: theme.palette.grey[700],
    },
    content: {
      justifyContent: 'left',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '8px',
    },
    contentUnit: {
      display: 'inline-block',
      textAlign: 'center',
      width: '33%',
    },
    contentUnitLabel: {
      fontSize: '12px',
      color: theme.palette.grey[700],
    },
  })

const LayersPopup = (props: ILayersPopup) => {
  const { t } = useTranslation()
  const stops: ILayersContent[] = [
    {
      layer: (
        <IconButton onClick={() => props.toggleLayer(Stops_LineTypeEnum.bus)}>
          <DirectionsBusIcon color={props.activeStopsLayers.includes(Stops_LineTypeEnum.bus) ? 'primary' : 'action'} />
        </IconButton>
      ),
      label: 'Bus',
    },
    {
      layer: (
        <IconButton onClick={() => props.toggleLayer(Stops_LineTypeEnum.subway)}>
          <SubwayIcon color={props.activeStopsLayers.includes(Stops_LineTypeEnum.subway) ? 'primary' : 'action'} />
        </IconButton>
      ),
      label: 'Subway',
    },
    {
      layer: (
        <IconButton onClick={() => props.toggleLayer(Stops_LineTypeEnum.train)}>
          <TrainIcon color={props.activeStopsLayers.includes(Stops_LineTypeEnum.train) ? 'primary' : 'action'} />
        </IconButton>
      ),
      label: 'Train',
    },
    {
      layer: (
        <IconButton onClick={() => props.toggleLayer(Stops_LineTypeEnum.tram)}>
          <TramIcon color={props.activeStopsLayers.includes(Stops_LineTypeEnum.tram) ? 'primary' : 'action'} />
        </IconButton>
      ),
      label: 'Tramway',
    },
    {
      layer: (
        <IconButton onClick={() => props.toggleLayer(Stops_LineTypeEnum.ferry)}>
          <DirectionsBoatIcon
            color={props.activeStopsLayers.includes(Stops_LineTypeEnum.ferry) ? 'primary' : 'action'}
          />
        </IconButton>
      ),
      label: 'Ferry',
    },
  ]

  const docked_stations: ILayersContent[] =
    (props.activeDockedStationLayers &&
      props.toggleDockedStationLayer && [
        {
          layer: (
            <IconButton onClick={() => props.toggleDockedStationLayer?.(DockedStationEnum.citibike)}>
              <PedalBikeIcon
                color={props.activeDockedStationLayers.includes(DockedStationEnum.citibike) ? 'primary' : 'action'}
              />
            </IconButton>
          ),
          label: t('common.dockedstations.cityBike'),
        },
      ]) ??
    []

  /*
  NEXT VERSION
  const lanes =     
    {
      layer: (
        <IconButton>
          <DirectionsBusIcon
            color={props.activeStopsLayers.includes(Stops_LineTypeEnum.bus) ? 'primary' : 'action'}
            onClick={() => props.toggleLayer(Stops_LineTypeEnum.bus)}
          />
        </IconButton>
      ),
      label: 'Bike',
    },
  */

  return (
    <div className={props.classes.root}>
      <div className={props.classes.title}>{'Infrastructure Layers'}</div>
      <div className={props.classes.content}>
        <div className={props.classes.subtitle}>{'Stops'}</div>
        <List>
          {stops.map((stop, index) => (
            <div key={index} className={props.classes.contentUnit}>
              <div>{stop.layer}</div>
              <div className={props.classes.contentUnitLabel}>{stop.label}</div>
            </div>
          ))}
        </List>
        {/*
        NEXT VERSION:
        <div className={props.classes.subtitle}>{'Lanes'}</div>
        <List>
          {lanes.map((lane, index) => (
            <div key={index} className={props.classes.contentUnit}>
              <div>{lane.layer}</div>
              <div className={props.classes.contentUnitLabel}>{lane.label}</div>
            </div>
          ))}
        </List>
        */}
        {docked_stations.length > 0 && (
          <>
            <div className={props.classes.subtitle}>{'Docked stations'}</div>
            <List>
              {docked_stations.map((station, index) => (
                <div key={index} className={props.classes.contentUnit}>
                  <div>{station.layer}</div>
                  <div className={props.classes.contentUnitLabel}>{station.label}</div>
                </div>
              ))}
            </List>
          </>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(LayersPopup)
